import { EngineType, SurfaceFunctionKey, LiquidGlassOptions } from '../types';
import { buildSvgFilterMarkup } from '../core/svg-engine';
import { WebGLGlassRenderer } from '../core/webgl-engine';

let instanceCounter = 0;
let globalSvgDefs: SVGDefsElement | null = null;

function getGlobalSvgDefs(): SVGDefsElement | null {
  if (typeof document === 'undefined') return null;
  if (globalSvgDefs && document.contains(globalSvgDefs)) {
    return globalSvgDefs;
  }
  let container = document.getElementById('lg-global-svg-defs') as SVGSVGElement | null;
  if (!container) {
    container = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    container.id = 'lg-global-svg-defs';
    container.setAttribute('aria-hidden', 'true');
    container.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;overflow:hidden;pointer-events:none;z-index:-9999;';
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    container.appendChild(defs);
    (document.body || document.documentElement).appendChild(container);
    globalSvgDefs = defs;
  } else {
    globalSvgDefs = container.querySelector('defs');
  }
  return globalSvgDefs;
}

export class LiquidGlassElement extends HTMLElement {
  public static get observedAttributes() {
    return [
      'width',
      'height',
      'radius',
      'thickness',
      'bezel',
      'ior',
      'scale-ratio',
      'blur',
      'specular-opacity',
      'specular-saturation',
      'shadow-color',
      'shadow-blur',
      'shadow-spread',
      'tint-color',
      'tint-opacity',
      'outer-shadow-blur',
      'draggable',
      'engine',
      'surface-fn',
      'options',
      'background-src',
      'background-url',
    ];
  }

  private filterId: string;
  private shadowRootRef: ShadowRoot;
  private glassBackdrop: HTMLDivElement;
  private webglRenderer: WebGLGlassRenderer | null = null;
  private webglCanvas: HTMLCanvasElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private onGlobalBgChange: ((e: Event) => void) | null = null;

  // Pointer drag state
  private isDragging = false;
  private isPointerDragInitialized = false;

  constructor() {
    super();
    instanceCounter++;
    this.filterId = `liquid-glass-filter-${instanceCounter}`;

    this.shadowRootRef = this.attachShadow({ mode: 'open' });
    this.shadowRootRef.innerHTML = `
      <style>
        :host {
          display: inline-flex;
          position: relative;
          box-sizing: border-box;
          border-radius: var(--lg-glass-radius, 20px);
          isolation: isolate;
          vertical-align: middle;
          cursor: default;
        }

        :host([draggable="true"]), :host([draggable=""]) {
          cursor: grab;
          touch-action: none;
          user-select: none;
          -webkit-user-drag: none;
        }

        :host([draggable="true"]:active), :host([draggable=""]:active) {
          cursor: grabbing;
        }

        :host([draggable="true"]) *, :host([draggable=""]) * {
          -webkit-user-drag: none;
          user-select: none;
        }

        .lg-backdrop {
          position: absolute;
          inset: 0;
          border-radius: inherit;
          isolation: isolate;
          touch-action: none;
          box-shadow: 0px 8px var(--lg-outer-shadow-blur, 28px) rgba(0, 0, 0, 0.35);
          overflow: hidden;
          pointer-events: none;
          z-index: 0;
          -webkit-transform: translate3d(0, 0, 0);
          transform: translate3d(0, 0, 0);
        }

        .lg-gl-canvas {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border-radius: inherit;
          display: none;
          pointer-events: none;
          z-index: 0;
        }

        .lg-backdrop::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 2;
          border-radius: inherit;
          border: 1.5px solid rgba(255, 255, 255, 0.35);
          box-shadow: 
            inset 0 0 var(--lg-shadow-blur, 20px) var(--lg-shadow-spread, -5px) var(--lg-shadow-color, rgba(255, 255, 255, 0.45)),
            inset 0 2px 4px rgba(255, 255, 255, 0.75),
            inset 0 -2px 4px rgba(0, 0, 0, 0.3),
            inset 2px 0 4px rgba(56, 189, 248, 0.25),
            inset -2px 0 4px rgba(244, 63, 94, 0.25);
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.02) 100%),
                      rgba(var(--lg-tint-color, 255, 255, 255), var(--lg-tint-opacity, 0.04));
          pointer-events: none;
        }

        .lg-backdrop::after {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 1;
          border-radius: inherit;
          isolation: isolate;
          -webkit-backdrop-filter: blur(max(var(--lg-blur, 0px), 14px)) saturate(180%) contrast(105%) brightness(105%);
          backdrop-filter: blur(max(var(--lg-blur, 0px), 14px)) saturate(180%) contrast(105%) brightness(105%);
          -webkit-backdrop-filter: blur(var(--lg-blur, 0px)) url(#${this.filterId});
          backdrop-filter: blur(var(--lg-blur, 0px)) url(#${this.filterId});
          -webkit-transform: translate3d(0, 0, 0);
          transform: translate3d(0, 0, 0);
        }

        .lg-content {
          position: relative;
          z-index: 10;
          box-sizing: border-box;
          width: 100%;
          height: 100%;
          pointer-events: auto;
          word-break: break-word;
        }
      </style>
      <svg class="lg-svg-defs" width="0" height="0" style="position: absolute; width: 0; height: 0; overflow: hidden; pointer-events: none;" color-interpolation-filters="sRGB">
        <defs></defs>
      </svg>
      <div class="lg-backdrop">
        <canvas class="lg-gl-canvas"></canvas>
      </div>
      <div class="lg-content">
        <slot></slot>
      </div>
    `;

    this.glassBackdrop = this.shadowRootRef.querySelector('.lg-backdrop') as HTMLDivElement;
  }

  private bgMutationObserver: MutationObserver | null = null;

  connectedCallback() {
    this.setupPointerDrag();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.width > 2 && entry.contentRect.height > 2) {
            requestAnimationFrame(() => this.renderGlass());
          }
        }
      });
      this.resizeObserver.observe(this);
    }

    if (typeof MutationObserver !== 'undefined') {
      const target = document.querySelector('.app-wallpaper-layer, .app-layout') || document.body;
      if (target) {
        this.bgMutationObserver = new MutationObserver(() => {
          this.invalidateWebGL();
          this.renderGlass();
        });
        this.bgMutationObserver.observe(target, {
          attributes: true,
          attributeFilter: ['class', 'style']
        });
      }
    }

    this.onGlobalBgChange = (e: Event) => {
      const ce = e as CustomEvent<{ src: string }>;
      if (ce.detail?.src) {
        this.setAttribute('background-src', ce.detail.src);
      }
      this.invalidateWebGL();
      this.renderGlass();
    };
    window.addEventListener('liquid-glass-bg-change', this.onGlobalBgChange);

    this.renderGlass();
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.bgMutationObserver) {
      this.bgMutationObserver.disconnect();
      this.bgMutationObserver = null;
    }
    if (this.onGlobalBgChange) {
      window.removeEventListener('liquid-glass-bg-change', this.onGlobalBgChange);
      this.onGlobalBgChange = null;
    }
    this.cleanupEngine();

    const globalDefs = getGlobalSvgDefs();
    if (globalDefs) {
      const existing = globalDefs.querySelector(`#${this.filterId}`);
      if (existing) {
        existing.remove();
      }
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue && this.isConnected) {
      if (name === 'options') {
        this.options = newValue;
      } else if (name === 'background-src' || name === 'background-url') {
        this.invalidateWebGL();
        this.renderGlass();
      } else {
        this.renderGlass();
      }
    }
  }

  get hasExplicitWidth(): boolean {
    const attr = this.getAttribute('width');
    return !(!attr || isNaN(parseFloat(attr)) || attr === 'auto');
  }

  get hasExplicitHeight(): boolean {
    const attr = this.getAttribute('height');
    return !(!attr || isNaN(parseFloat(attr)) || attr === 'auto');
  }

  get glassWidth(): number {
    if (this.hasExplicitWidth) {
      return parseFloat(this.getAttribute('width')!);
    }
    const rect = this.getBoundingClientRect();
    return rect.width > 2 ? Math.round(rect.width) : (this.offsetWidth || 180);
  }
  set glassWidth(val: number) {
    this.setAttribute('width', val.toString());
  }

  get glassHeight(): number {
    if (this.hasExplicitHeight) {
      return parseFloat(this.getAttribute('height')!);
    }
    const rect = this.getBoundingClientRect();
    return rect.height > 2 ? Math.round(rect.height) : (this.offsetHeight || 50);
  }
  set glassHeight(val: number) {
    this.setAttribute('height', val.toString());
  }

  get radius(): number {
    return parseFloat(this.getAttribute('radius') || '24');
  }
  set radius(val: number) {
    this.setAttribute('radius', val.toString());
  }

  get thickness(): number {
    return parseFloat(this.getAttribute('thickness') || '80');
  }
  set thickness(val: number) {
    this.setAttribute('thickness', val.toString());
  }

  get bezel(): number {
    return parseFloat(this.getAttribute('bezel') || '60');
  }
  set bezel(val: number) {
    this.setAttribute('bezel', val.toString());
  }

  get ior(): number {
    return parseFloat(this.getAttribute('ior') || '3.0');
  }
  set ior(val: number) {
    this.setAttribute('ior', val.toString());
  }

  get scaleRatio(): number {
    return parseFloat(this.getAttribute('scale-ratio') || '1.0');
  }
  set scaleRatio(val: number) {
    this.setAttribute('scale-ratio', val.toString());
  }

  get blurAmount(): number {
    return parseFloat(this.getAttribute('blur') || '0');
  }
  set blurAmount(val: number) {
    this.setAttribute('blur', val.toString());
  }

  get specularOpacity(): number {
    return parseFloat(this.getAttribute('specular-opacity') || '0');
  }
  set specularOpacity(val: number) {
    this.setAttribute('specular-opacity', val.toString());
  }

  get specularSaturation(): number {
    return parseFloat(this.getAttribute('specular-saturation') || '4');
  }
  set specularSaturation(val: number) {
    this.setAttribute('specular-saturation', val.toString());
  }

  get shadowColor(): string {
    return this.getAttribute('shadow-color') || '#ffffff';
  }
  set shadowColor(val: string) {
    this.setAttribute('shadow-color', val);
  }

  get shadowBlur(): number {
    return parseFloat(this.getAttribute('shadow-blur') || '20');
  }
  set shadowBlur(val: number) {
    this.setAttribute('shadow-blur', val.toString());
  }

  get shadowSpread(): number {
    return parseFloat(this.getAttribute('shadow-spread') || '-5');
  }
  set shadowSpread(val: number) {
    this.setAttribute('shadow-spread', val.toString());
  }

  get tintColor(): string {
    return this.getAttribute('tint-color') || '#ffffff';
  }
  set tintColor(val: string) {
    this.setAttribute('tint-color', val);
  }

  get tintOpacity(): number {
    return parseFloat(this.getAttribute('tint-opacity') || '0');
  }
  set tintOpacity(val: number) {
    this.setAttribute('tint-opacity', val.toString());
  }

  get outerShadowBlur(): number {
    return parseFloat(this.getAttribute('outer-shadow-blur') || '24');
  }
  set outerShadowBlur(val: number) {
    this.setAttribute('outer-shadow-blur', val.toString());
  }

  get isDraggable(): boolean {
    return this.hasAttribute('draggable') && this.getAttribute('draggable') !== 'false';
  }
  set isDraggable(val: boolean) {
    if (val) this.setAttribute('draggable', 'true');
    else this.removeAttribute('draggable');
  }

  get engine(): EngineType {
    return (this.getAttribute('engine') as EngineType) || 'svg';
  }
  set engine(val: EngineType) {
    this.setAttribute('engine', val);
  }

  get surfaceFn(): SurfaceFunctionKey {
    return (this.getAttribute('surface-fn') as SurfaceFunctionKey) || 'convex_squircle';
  }
  set surfaceFn(val: SurfaceFunctionKey) {
    this.setAttribute('surface-fn', val);
  }

  get backgroundSrc(): string {
    return this.getAttribute('background-src') || this.getAttribute('background-url') || '';
  }
  set backgroundSrc(val: string) {
    if (val) this.setAttribute('background-src', val);
    else this.removeAttribute('background-src');
  }

  get backgroundUrl(): string {
    return this.backgroundSrc;
  }
  set backgroundUrl(val: string) {
    this.backgroundSrc = val;
  }

  get options(): LiquidGlassOptions {
    return {
      width: this.hasExplicitWidth ? this.glassWidth : undefined,
      height: this.hasExplicitHeight ? this.glassHeight : undefined,
      radius: this.radius,
      thickness: this.thickness,
      bezel: this.bezel,
      ior: this.ior,
      blur: this.blurAmount,
      specularOpacity: this.specularOpacity,
      specularSaturation: this.specularSaturation,
      shadowColor: this.shadowColor,
      shadowBlur: this.shadowBlur,
      shadowSpread: this.shadowSpread,
      tintColor: this.tintColor,
      tintOpacity: this.tintOpacity,
      outerShadowBlur: this.outerShadowBlur,
      draggable: this.isDraggable,
      engine: this.engine,
      surfaceFn: this.surfaceFn,
    };
  }

  set options(opts: LiquidGlassOptions | string) {
    if (!opts) return;
    try {
      const parsed: LiquidGlassOptions = typeof opts === 'string' ? JSON.parse(opts) : opts;
      if (parsed.width !== undefined) this.setAttribute('width', parsed.width.toString());
      if (parsed.height !== undefined) this.setAttribute('height', parsed.height.toString());
      if (parsed.radius !== undefined) this.setAttribute('radius', parsed.radius.toString());
      if (parsed.thickness !== undefined) this.setAttribute('thickness', parsed.thickness.toString());
      if (parsed.bezel !== undefined) this.setAttribute('bezel', parsed.bezel.toString());
      if (parsed.ior !== undefined) this.setAttribute('ior', parsed.ior.toString());
      if (parsed.blur !== undefined) this.setAttribute('blur', parsed.blur.toString());
      if (parsed.scaleRatio !== undefined) this.setAttribute('scale-ratio', parsed.scaleRatio.toString());
      if (parsed.specularOpacity !== undefined) this.setAttribute('specular-opacity', parsed.specularOpacity.toString());
      if (parsed.specularSaturation !== undefined) this.setAttribute('specular-saturation', parsed.specularSaturation.toString());
      if (parsed.shadowColor !== undefined) this.setAttribute('shadow-color', parsed.shadowColor);
      if (parsed.shadowBlur !== undefined) this.setAttribute('shadow-blur', parsed.shadowBlur.toString());
      if (parsed.shadowSpread !== undefined) this.setAttribute('shadow-spread', parsed.shadowSpread.toString());
      if (parsed.tintColor !== undefined) this.setAttribute('tint-color', parsed.tintColor);
      if (parsed.tintOpacity !== undefined) this.setAttribute('tint-opacity', parsed.tintOpacity.toString());
      if (parsed.outerShadowBlur !== undefined) this.setAttribute('outer-shadow-blur', parsed.outerShadowBlur.toString());
      if (parsed.draggable !== undefined) {
        if (parsed.draggable) this.setAttribute('draggable', 'true');
        else this.removeAttribute('draggable');
      }
      if (parsed.engine !== undefined) this.setAttribute('engine', parsed.engine);
      if (parsed.surfaceFn !== undefined) this.setAttribute('surface-fn', parsed.surfaceFn);
      this.renderGlass();
    } catch {
      // ignore parse error
    }
  }

  private hexToRgb(hex: string): string {
    if (!hex.startsWith('#')) return '255, 255, 255';
    const cleanHex = hex.replace('#', '');
    const fullHex = cleanHex.length === 3 ? cleanHex.split('').map((c) => c + c).join('') : cleanHex;
    const r = parseInt(fullHex.slice(0, 2), 16) || 255;
    const g = parseInt(fullHex.slice(2, 4), 16) || 255;
    const b = parseInt(fullHex.slice(4, 6), 16) || 255;
    return `${r}, ${g}, ${b}`;
  }

  private setupPointerDrag() {
    if (this.isPointerDragInitialized) return;
    this.isPointerDragInitialized = true;

    // Prevent native browser drag & drop
    this.addEventListener('dragstart', (e: DragEvent) => {
      if (this.isDraggable) {
        e.preventDefault();
      }
    }, true);

    this.addEventListener('pointerdown', (e: PointerEvent) => {
      if (!this.isDraggable) return;
      // Only drag on primary mouse button (left click) or touch/pen
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      if ((e.target as HTMLElement).closest('button, a, input, textarea, select, [data-no-drag]')) return;

      const startX = e.clientX;
      const startY = e.clientY;
      let hasStartedDrag = false;
      let offsetLeft = 0;
      let offsetTop = 0;

      let rafId: number | null = null;
      let targetLeft = 0;
      let targetTop = 0;

      const onMove = (moveEv: PointerEvent) => {
        const dx = moveEv.clientX - startX;
        const dy = moveEv.clientY - startY;

        if (!hasStartedDrag) {
          if (Math.hypot(dx, dy) < 2) return;
          hasStartedDrag = true;
          this.isDragging = true;
          const rect = this.getBoundingClientRect();
          offsetLeft = startX - rect.left;
          offsetTop = startY - rect.top;

          this.style.position = 'fixed';
          this.style.left = `${rect.left}px`;
          this.style.top = `${rect.top}px`;
          this.style.margin = '0';
          this.style.zIndex = '9999';
          this.style.cursor = 'grabbing';
          this.style.userSelect = 'none';
          this.style.willChange = 'left, top, transform';

          try {
            this.setPointerCapture(e.pointerId);
          } catch {}
        }

        moveEv.preventDefault();
        let newLeft = moveEv.clientX - offsetLeft;
        let newTop = moveEv.clientY - offsetTop;

        const parentBoundary = this.closest('[data-drag-boundary]') as HTMLElement | null;
        if (parentBoundary) {
          const boundaryRect = parentBoundary.getBoundingClientRect();
          const currentW = this.getBoundingClientRect().width || this.glassWidth || 300;
          const currentH = this.getBoundingClientRect().height || this.glassHeight || 200;
          const minLeft = boundaryRect.left;
          const maxLeft = Math.max(minLeft, boundaryRect.right - currentW);
          const minTop = boundaryRect.top;
          const maxTop = Math.max(minTop, boundaryRect.bottom - currentH);
          newLeft = Math.max(minLeft, Math.min(maxLeft, newLeft));
          newTop = Math.max(minTop, Math.min(maxTop, newTop));
        }

        targetLeft = newLeft;
        targetTop = newTop;

        if (!rafId) {
          rafId = requestAnimationFrame(() => {
            this.style.left = `${targetLeft}px`;
            this.style.top = `${targetTop}px`;
            if (this.webglRenderer) {
              const currentRect = this.getBoundingClientRect();
              this.webglRenderer.update(currentRect, {
                radius: this.radius,
                thickness: this.thickness,
                bezel: this.bezel,
                ior: this.ior,
                blur: this.blurAmount,
                specular: this.specularOpacity,
                tint: this.tintOpacity / 100,
              });
            }
            rafId = null;
          });
        }
      };

      const onUp = () => {
        try {
          this.releasePointerCapture(e.pointerId);
        } catch {}

        if (hasStartedDrag) {
          this.isDragging = false;
          this.style.cursor = '';
          this.style.userSelect = '';
        }
        window.removeEventListener('pointermove', onMove, true);
        window.removeEventListener('pointerup', onUp, true);
        window.removeEventListener('pointercancel', onUp, true);
      };

      window.addEventListener('pointermove', onMove, { passive: false, capture: true });
      window.addEventListener('pointerup', onUp, { capture: true, once: true });
      window.addEventListener('pointercancel', onUp, { capture: true, once: true });
    });
  }

  private get isSafariBrowser(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  private get effectiveEngine(): EngineType {
    if (this.engine === 'webgl') return 'webgl';
    if (this.isSafariBrowser) return 'webgl';
    return 'svg';
  }

  private cleanupEngine() {
    if (this.webglRenderer) {
      this.webglRenderer.destroy();
      this.webglRenderer = null;
    }
    if (this.webglCanvas) {
      this.webglCanvas.style.display = 'none';
    }
  }

  /** Force WebGL to re-fetch the background texture on next render */
  public invalidateWebGL() {
    if (this.webglRenderer) {
      this.webglRenderer.invalidateBackground();
    }
  }

  public renderGlass() {
    if (!this.isDragging) {
      if (this.hasExplicitWidth) {
        this.style.width = `${parseFloat(this.getAttribute('width')!)}px`;
      } else {
        this.style.width = 'fit-content';
      }

      if (this.hasExplicitHeight) {
        this.style.height = `${parseFloat(this.getAttribute('height')!)}px`;
      } else {
        this.style.height = 'auto';
      }
    }

    const rect = this.getBoundingClientRect();
    const measuredW = Math.round(rect.width) || this.offsetWidth;
    const measuredH = Math.round(rect.height) || this.offsetHeight;

    if (measuredW < 2 || measuredH < 2) return;

    const w = this.hasExplicitWidth ? this.glassWidth : measuredW;
    const h = this.hasExplicitHeight ? this.glassHeight : measuredH;
    const br = Math.min(this.radius, Math.floor(Math.min(w, h) / 2));

    this.style.setProperty('--lg-glass-radius', `${br}px`);
    this.style.setProperty('--lg-blur', `${this.blurAmount}px`);
    this.glassBackdrop.style.setProperty('--lg-shadow-color', this.shadowColor);
    this.glassBackdrop.style.setProperty('--lg-shadow-blur', `${this.shadowBlur}px`);
    this.glassBackdrop.style.setProperty('--lg-shadow-spread', `${this.shadowSpread}px`);
    this.glassBackdrop.style.setProperty('--lg-tint-color', this.hexToRgb(this.tintColor));
    this.glassBackdrop.style.setProperty('--lg-tint-opacity', (this.tintOpacity / 100).toFixed(3));
    this.glassBackdrop.style.setProperty('--lg-outer-shadow-blur', `${this.outerShadowBlur}px`);

    if (this.effectiveEngine === 'webgl') {
      this.renderWebGL(w, h, br);
    } else {
      this.renderSVG(w, h, br);
    }
  }

  private renderSVG(w: number, h: number, br: number) {
    if (this.webglRenderer) {
      this.cleanupEngine();
    }

    if (w <= 2 || h <= 2) return;

    const filterMarkup = buildSvgFilterMarkup(
      this.filterId,
      w,
      h,
      br,
      this.thickness,
      this.bezel,
      this.ior,
      this.scaleRatio,
      this.blurAmount,
      this.specularOpacity,
      this.specularSaturation,
      this.surfaceFn
    );

    // 1. Inject into Global Document Defs (required for Chrome/Safari/Firefox CSS backdrop-filter: url(#id))
    const globalDefs = getGlobalSvgDefs();
    if (globalDefs) {
      const existing = globalDefs.querySelector(`#${this.filterId}`);
      if (existing) {
        existing.remove();
      }
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(
        `<svg xmlns="http://www.w3.org/2000/svg">${filterMarkup}</svg>`,
        'image/svg+xml'
      );
      const newFilter = svgDoc.querySelector('filter');
      if (newFilter) {
        const imported = document.importNode(newFilter, true);
        globalDefs.appendChild(imported);
      }
    }

    // 2. Also keep inside ShadowRoot defs
    const shadowDefs = this.shadowRootRef.querySelector('defs');
    if (shadowDefs) {
      shadowDefs.innerHTML = filterMarkup;
    }
  }

  private renderWebGL(_w: number, _h: number, br: number) {
    if (!this.webglCanvas) {
      this.webglCanvas = this.shadowRootRef.querySelector('.lg-gl-canvas') as HTMLCanvasElement;
    }
    if (this.webglCanvas) {
      this.webglCanvas.style.display = 'block';
      if (!this.webglRenderer) {
        this.webglRenderer = new WebGLGlassRenderer(this.webglCanvas);
      }
    }

    // Hide CSS backdrop-filter when WebGL canvas is actively rendering refraction
    this.glassBackdrop.style.setProperty('backdrop-filter', 'none');
    this.glassBackdrop.style.setProperty('-webkit-backdrop-filter', 'none');

    // Look for background image — check explicit background-src FIRST
    let bgUrl = this.backgroundSrc;

    // 1. Check dedicated wallpaper layer (separate fixed div)
    if (!bgUrl) {
      const wpEl = document.querySelector('.app-wallpaper-layer') as HTMLElement | null;
      if (wpEl) {
        const bg = window.getComputedStyle(wpEl).backgroundImage;
        if (bg && bg !== 'none' && bg.includes('url(')) {
          const match = bg.match(/url\(["']?([^"')]+)["']?\)/);
          if (match && match[1]) bgUrl = match[1];
        }
      }
    }

    // 2. Walk up ancestors
    if (!bgUrl) {
      let curr: HTMLElement | null = this;
      while (curr) {
        const bg = window.getComputedStyle(curr).backgroundImage;
        if (bg && bg !== 'none' && bg.includes('url(')) {
          const match = bg.match(/url\(["']?([^"')]+)["']?\)/);
          if (match && match[1]) {
            bgUrl = match[1];
            break;
          }
        }
        curr = curr.parentElement;
      }
    }

    // 3. Check .app-layout or body as last resort
    if (!bgUrl) {
      const appEl = document.querySelector('.app-layout') as HTMLElement | null;
      if (appEl) {
        const bg = window.getComputedStyle(appEl).backgroundImage;
        if (bg && bg !== 'none') {
          const match = bg.match(/url\(["']?([^"')]+)["']?\)/);
          if (match && match[1]) bgUrl = match[1];
        }
      }
    }
    if (!bgUrl) {
      const bodyBg = window.getComputedStyle(document.body).backgroundImage;
      if (bodyBg && bodyBg !== 'none') {
        const match = bodyBg.match(/url\(["']?([^"')]+)["']?\)/);
        if (match && match[1]) bgUrl = match[1];
      }
    }

    if (bgUrl && this.webglRenderer) {
      this.webglRenderer.loadBackground(bgUrl);
    }

    const rect = this.getBoundingClientRect();
    this.webglRenderer?.update(rect, {
      radius: br,
      thickness: this.thickness,
      bezel: this.bezel,
      ior: this.ior,
      blur: this.blurAmount,
      specular: this.specularOpacity,
      tint: this.tintOpacity / 100,
    });
  }
}
